import { useEffect, useState } from 'react';
import useAttachment from 'pages/Forms/RegisterForm/CustomFormBuilder/components/UploadAttachments/hooks/useAttachment';
import { AttachmentFile, DataItem } from 'pages/Forms/FormResults/types/types';

interface AttachmentResultReturn {
  attachFile: AttachmentFile[] | undefined | null;
  handleDownload: (url: string) => void;
}

export const useAttachmentResult = (item: DataItem): AttachmentResultReturn => {
  const {
    fetchAttachmentData,
    stripPrefixInAttachmentID,
    removeSurroundingQuotes,
  } = useAttachment();

  const [attachFile, setAttachFile] = useState<AttachmentFile[]>([]);

  // Extract attachment IDs
  const getAttachmentIds = (): string[] => {
    if (Array.isArray(item?.answer)) {
      return item.answer
        .map((answerItem) =>
          stripPrefixInAttachmentID(
            answerItem?.attachmentId ?? answerItem?.id ?? '',
          ),
        )
        .filter(Boolean);
    }
    return typeof item?.answer === 'object' &&
      (item?.answer?.attachmentId || item?.answer?.id)
      ? [stripPrefixInAttachmentID(item.answer.attachmentId ?? item.answer.id)]
      : [];
  };

  useEffect(() => {
    if (!item?.answer) return;

    const attachmentIds = getAttachmentIds();

    fetchAttachmentData({
      query: { attachments: attachmentIds },
      skip: false,
      onSuccess: (data: Record<string, AttachmentFile>) => {
        const formattedAttachments = Object.keys(data).map((itemId) => {
          const signatoryInfo = Array.isArray(item.answer)
            ? item.answer.find((itm) => itm?.attachmentId === itemId)
            : (item.answer as AttachmentFile)?.attachmentId === itemId
              ? item.answer
              : undefined;

          return {
            ...data[itemId],
            id: removeSurroundingQuotes(String(itemId)),
            attachmentId: removeSurroundingQuotes(String(itemId)),
            signatory_name:
              (signatoryInfo as AttachmentFile)?.signatory_name ?? '',
            created_at: (signatoryInfo as AttachmentFile)?.created_at ?? '',
          };
        });
        // Sort the attachments by created_at in ascending order
        formattedAttachments?.sort((attachmentA, attachmentB) =>
          (attachmentA?.created_at ?? 0).localeCompare(
            attachmentB?.created_at ?? 0,
          ),
        );
        setAttachFile(formattedAttachments);
      },
    });
  }, [item?.answer]);

  const handleDownload = (url: string): void => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return { attachFile, handleDownload };
};
